import PostModel from "app/models/PostModel";
import { store } from "app/Store";
import { localGet, localSet } from "../LocalStorage";
import moment from "moment";
import { MutableRefObject, useCallback, useMemo } from "react";
import { shallowEqual, useSelector } from "react-redux";
import { RootState } from "app/reducers";
import { sendPostViews } from "app/actions/posts";

export interface IPostView {
  post_id: string;
  seen_at: string;
  viewed_at?: string;
}

// Half the post on screen for 800ms — slightly snappier than the IAB
// viewability baseline (50% for 1s) while still filtering scroll-pasts.
export const VIEWABLE_RATIO = 0.5;
export const VIEWABLE_DWELL_MS = 800;

const QUEUE_KEY = "postViews";
const QUEUE_OWNER_KEY = "postViewsOwner";
// Bounds re-queued batches so a persistently failing endpoint cannot grow the
// queue without limit; the newest views win.
const MAX_QUEUE_LENGTH = 100;
const OBSERVER_THRESHOLDS = [0, 0.1, 0.25, 0.5, 0.75, 1];

const postsByElement = new WeakMap<Element, PostModel>();
const dwellTimers = new Map<Element, ReturnType<typeof setTimeout>>();
const recordedPostIds = new Set<string>();

const readQueue = (): Array<IPostView> => localGet(QUEUE_KEY, []);
const writeQueue = (views: Array<IPostView>) => localSet(QUEUE_KEY, views);

function isViewable(entry: IntersectionObserverEntry): boolean {
  if (!entry.isIntersecting) {
    return false;
  }

  if (entry.intersectionRatio >= VIEWABLE_RATIO) {
    return true;
  }

  // A post taller than the viewport can never fill half of itself, so measure
  // it against the viewport instead.
  const rootHeight = entry.rootBounds
    ? entry.rootBounds.height
    : window.innerHeight;

  return (
    entry.boundingClientRect.height > rootHeight &&
    entry.intersectionRect.height >= rootHeight * VIEWABLE_RATIO
  );
}

/**
 * Live-geometry version of `isViewable`, checked when the dwell timer fires.
 * The observer's exit entries are delivered asynchronously and get starved
 * while a fast scroll janks the main thread, so a fly-past post's timer can
 * outlive its stale "viewable" state and record a post the user never saw.
 */
function isViewableNow(target: Element): boolean {
  const rect = target.getBoundingClientRect();
  const viewportHeight = window.innerHeight;
  const visibleHeight =
    Math.min(rect.bottom, viewportHeight) - Math.max(rect.top, 0);

  if (visibleHeight <= 0) {
    return false;
  }

  if (visibleHeight >= rect.height * VIEWABLE_RATIO) {
    return true;
  }

  return (
    rect.height > viewportHeight &&
    visibleHeight >= viewportHeight * VIEWABLE_RATIO
  );
}

function cancelDwell(target: Element) {
  clearTimeout(dwellTimers.get(target));
  dwellTimers.delete(target);
}

function toPostView(post: PostModel): IPostView {
  const now = moment().toISOString();

  return post.type === "note"
    ? { post_id: post.id, seen_at: now, viewed_at: now }
    : { post_id: post.id, seen_at: now };
}

function isTrackable(post: PostModel, viewerId: string): boolean {
  return (
    !recordedPostIds.has(post.id) &&
    !post.seenAt &&
    post.userId !== viewerId &&
    !readQueue().some((view) => view.post_id === post.id)
  );
}

function resetTrackingForUser(userId: string) {
  if (!userId || localGet(QUEUE_OWNER_KEY, null) === userId) {
    return;
  }

  // The queue belongs to another user (SPA logout has no reload, and on a
  // shared device a reload wipes module state but not localStorage) or its
  // owner is unknown — never send it under this user's token.
  writeQueue([]);
  localSet(QUEUE_OWNER_KEY, userId);
  recordedPostIds.clear();
}

function recordView(target: Element) {
  const post = postsByElement.get(target);

  if (!post) {
    return;
  }

  recordedPostIds.add(post.id);
  writeQueue([...readQueue(), toPostView(post)]);
}

function takePendingViews(): Array<IPostView> {
  const pending = readQueue();

  if (pending.length > 0) {
    // Observers append while a flush is in flight, so drop only what was taken.
    writeQueue(readQueue().slice(pending.length));
  }

  return pending;
}

export function usePostViewsTracking() {
  const { self } = useSelector((state: RootState) => state.user, shallowEqual);

  const observer = useMemo(
    () =>
      new IntersectionObserver(
        (entries) => entries.forEach(handleIntersection),
        { threshold: OBSERVER_THRESHOLDS }
      ),
    []
  );

  function handleIntersection(entry: IntersectionObserverEntry) {
    const target = entry.target;

    if (!isViewable(entry)) {
      cancelDwell(target);
      return;
    }

    if (!dwellTimers.has(target)) {
      dwellTimers.set(
        target,
        setTimeout(() => completeDwell(target), VIEWABLE_DWELL_MS)
      );
    }
  }

  function completeDwell(target: Element) {
    dwellTimers.delete(target);

    // Unmounted mid-dwell: it can never intersect again, so let it go.
    if (!target.isConnected) {
      observer.unobserve(target);
      return;
    }

    // Hidden tab: stay observed so returning to the tab can re-arm it.
    if (document.hidden) {
      return;
    }

    // The observer said "viewable" a second ago, but its exit entries lag
    // behind a fast scroll — only record if the post is still on screen NOW.
    // Stay observed: the pending exit entry re-syncs state, and a real return
    // to the post re-arms the dwell.
    if (!isViewableNow(target)) {
      return;
    }

    observer.unobserve(target);
    recordView(target);
  }

  // Stable per self.id: Post lists this in a useEffect dependency array, so a
  // fresh identity every render would re-run the effect — and its localStorage
  // queue read — on every render of every post. The identity must still change
  // when self.id does: observation deliberately starts on the re-render after
  // the account fetch resolves.
  const createObserver = useCallback(
    (elem: MutableRefObject<Element>, post: PostModel) => {
      // Until the account fetch resolves, self is null and every post — the
      // viewer's own included — would read as foreign.
      if (!self?.id || !elem.current) {
        return undefined;
      }

      resetTrackingForUser(self.id);

      if (!isTrackable(post, self.id)) {
        return undefined;
      }

      const target = elem.current;
      postsByElement.set(target, post);
      observer.observe(target);

      // Effect cleanup: a post that unmounts before its view is recorded must
      // release its observation and any in-flight dwell timer, instead of
      // lingering as a detached observed node until GC.
      return () => {
        cancelDwell(target);
        observer.unobserve(target);
      };
    },
    [self?.id]
  );

  // Stable forever: everything is read at call time, so there is no render
  // state to go stale.
  const sendAndClearPostViews = useCallback(() => {
    // This flush runs from App's interval and unload listeners, which fire on
    // routes with no posts — where createObserver never validates queue
    // ownership. Enforce it here too, or a previous user's queue could be
    // sent under the current user's token. Read from the store, not the
    // hook's `self`: App pins this closure at mount, when self is still null.
    const userId = store.getState().user.self?.id;

    if (!userId) {
      return;
    }

    resetTrackingForUser(userId);

    const pending = takePendingViews();

    if (pending.length === 0) {
      return;
    }

    // A failed send puts the batch back for the next poll tick, ahead of any
    // views recorded in the meantime, so a network blip loses nothing.
    sendPostViews(pending).catch(() => {
      writeQueue([...pending, ...readQueue()].slice(-MAX_QUEUE_LENGTH));
    });
  }, []);

  return {
    createObserver,
    sendAndClearPostViews,
  };
}
